/**
 * FRONT-2594 — save an open Canvas draft BEFORE Eddy reads it.
 *
 * Eddy reads a document from the server. A draft the person has typed in the
 * Canvas editor but not saved lives only in their browser, so a chat message
 * sent now would make Eddy answer about (or revise!) a copy that no longer
 * matches the screen. So a send first runs the editor's own version-checked save
 * (the one the Save button runs) and goes out only when the server holds the
 * text. When the save cannot finish, nothing is sent and the typed message
 * stays in the composer.
 *
 * `CanvasFlush` is what the mounted editor registers; null means no editor is
 * mounted (flag off, panel closed, a sketch or deck nobody has edited), so
 * there is no draft to save and no save is attempted. A sketch or deck editor
 * with unsaved text STAYS mounted even if the item stops being editable, so its
 * flush is still registered and a send still saves the draft first.
 */
export type CanvasFlushResult = 'saved' | 'clean' | 'conflict' | 'failed';

export type CanvasFlush = () => Promise<CanvasFlushResult>;

/** Shown beside the composer when a send was held back. Exact wording is part of the contract. */
export const CANVAS_SEND_HOLD_HINT = 'Save or discard your Canvas edits first.';

export type CanvasSendGate = { send: true; saved: boolean } | { send: false };

export async function gateSendOnCanvasDraft(flush: CanvasFlush | null): Promise<CanvasSendGate> {
  if (!flush) return { send: true, saved: false };
  let result: CanvasFlushResult;
  try {
    result = await flush();
  } catch {
    result = 'failed';
  }
  if (result === 'saved') return { send: true, saved: true };
  if (result === 'clean') return { send: true, saved: false };
  return { send: false };
}
